//
// # Schematic "Manager"
// Mostly the thing that knows how to apply changes to the schematic data model.
// Similar to LayoutManager but for schematic operations.
//

// Local Imports
import { Result, exhaust } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { IdMap } from "../id";
import { Ident } from "../ident";
import { SchematicChange as Change } from "./change";
import { Schematic, SchematicJson } from "./schematic";
import { Instance } from "./instance";
import { SchPinSpec } from "./schport";
import { WireSegment } from "./wire";

class Instances extends IdMap<schema.schematic.Instance> {}
class Pins extends IdMap<schema.schematic.SchPin> {}
class Wires extends IdMap<schema.schematic.WireSegment> {}

export class SchematicManager {
  private constructor(readonly ident: Ident) {}

  // Create a new SchematicManager from a Schematic
  static new(content: schema.Schematic) {
    const me = new SchematicManager(content.ident);

    // Convert JSON instances to our in-memory format
    content.instances.forEach((inst) => me.instances.add(inst));
    content.pins.forEach((pin) => me.pins.add(pin));
    content.wireSegments.forEach((wire) => me.wires.add(wire));

    return me;
  }

  readonly instances = new Instances();
  readonly pins = new Pins();
  readonly wires = new Wires();
  readonly changeManager = new ChangeManager(this);

  readonly export = (): schema.Schematic => {
    return {
      schemaVersion: schema.SCHEMA_VERSION,
      ident: this.ident,
      contentKind: "schematic",
      instances: Array.from(this.instances.values()),
      pins: Array.from(this.pins.values()),
      wireSegments: Array.from(this.wires.values()),
      graphics: [], // FIXME: handle graphics properly
      properties: [],
    };
  };

  applyChangeRequest = async (
    changeList: Array<unknown>
  ): Promise<Result<unknown>> => {
    const result = this.changeManager.applyChangeRequest(changeList);
    return result;
  };
}

export class ChangeManager {
  constructor(private mgr: SchematicManager) {}

  //
  // # Convert a change request
  //
  // Main entry point for most of this class's action.
  // Any array of (anythings) comes in, we validate they are valid changes,
  // look up all the things they refer to, and if successful,
  // return a list of in-memory changes class instances.
  //
  readonly applyChangeRequest = async (
    changeList: Array<unknown>
  ): Promise<Result<null>> => {
    //
    // Handle empty change list as a valid case
    if (!changeList || changeList.length === 0) {
      return Result.Ok(null);
    }

    // Do two steps of conversion: validation, then to our in-memory model
    const { validate } = Change.validator;
    const validations = changeList.map(validate);

    const promises = validations.map((r) => r.andThenAsync(this.applyChange));

    // And this next bit is basically `Result.all` - but we want to keep track of the errors
    const resultsList = await Promise.all(promises);
    const oks = resultsList.filter((r) => r.ok).map((r) => r.val);
    const errs = resultsList.filter((r) => r.err).map((r) => r.val);

    if (errs && errs.length > 0) {
      return Result.Err({ why: "error-applying-changes", errs });
    }

    return Result.Ok(null);
  };

  // Convert a (serializable) `Change` into an in-memory one
  private applyChange = async (change: Change): Promise<Result<null>> => {
    //
    switch (change.changeKind) {
      case "add":
        return this.applyAddChange(change);
      case "update":
        return this.applyUpdateChange(change);
      case "remove":
        return this.applyRemoveChange(change);
      case "unsupported":
        return Result.Err({
          why: "unsupported",
          message: "Unsupported Change Request",
        });
      default:
        exhaust(change);
    }
  };

  // Convert a `remove` to an in-memory `Change`.
  // Mostly searches for the entity to remove, and returns an error if it can't find it.
  private applyRemoveChange = (
    change: Change & { changeKind: "remove" }
  ): Result<null> => {
    const { id } = change;
    const { targetKind } = change.data;

    const find = () => {
      switch (targetKind) {
        case "instance":
          const instanceResult = this.mgr.instances.remove(id);
          return instanceResult;
        case "schpin":
          const pinResult = this.mgr.pins.remove(id);
          return pinResult;
        case "wire":
          const wireResult = this.mgr.wires.remove(id);
          return wireResult;
        case "graphics":
          // Annotation graphics are handled by EditorCore's annotations layer /
          // server state, not this model-level manager. No-op success.
          return true;
        case "constraint":
        case "constraint_group":
          // Constraints and groups are handled server-side, not in the client-side manager
          return false;
        default:
          exhaust(targetKind);
      }
    };
    const result = find()
      ? Result.Ok(null)
      : Result.Err({
          why: "invalid-change-entity-not-found",
          message: "Remove non-existent entity",
        });
    return result;
  };

  private applyUpdateChange = async (
    change: Change & { changeKind: "update" }
  ): Promise<Result<null>> => {
    const { id, data } = change;
    const { targetKind } = data;
    if (id === undefined) {
      return Result.Err({
        why: "invalid-change-entity-no-id",
        message: "Update entity with no ID",
      });
    }
    const find = () => {
      switch (targetKind) {
        case "instance":
          return this.mgr.instances.update({ ...data, id });
        case "schpin":
          return this.mgr.pins.update({ ...data, id });
        case "wire":
          return this.mgr.wires.update({ ...data, id });
        case "graphics":
        case "constraint":
        case "constraint_group":
          // Annotation graphics, constraints and groups are handled elsewhere
          // (EditorCore annotations / server state), not this manager.
          return true;
        default:
          return false;
      }
    };
    return find()
      ? Result.Ok(null)
      : Result.Err({
          why: "invalid-change-entity-not-found",
          message: "Update non-existent entity",
        });
  };

  private applyAddChange = async (
    change: Change & { changeKind: "add" }
  ): Promise<Result<null>> => {
    const { data } = change;
    const { targetKind } = data;
    switch (targetKind) {
      case "instance":
        this.mgr.instances.add(data);
        break;
      case "schpin":
        this.mgr.pins.add(data);
        break;
      case "wire":
        this.mgr.wires.add(data);
        break;
      case "graphics":
      case "constraint":
      case "constraint_group":
        // Annotation graphics, constraints and groups are handled elsewhere
        // (EditorCore annotations / server state), not this manager.
        break;
      default:
        exhaust(targetKind);
    }
    // add() stores the entity in the map. For CRDTMap, if there's an ID collision
    // with an entity that has a newer timestamp, the add is silently ignored per
    // CRDT semantics (newer timestamp wins). This is intentional conflict resolution.
    return Result.Ok(null);
  };
}
